'use client';

import { useEffect, useRef } from 'react';

import clsx from 'clsx';

import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp } from './Arrows';

import styles from './Arrow.module.scss';

type ArrowProps = {
  /**
   * The direction of the arrow.
   */
  direction: 'up' | 'down' | 'left' | 'right';
  /**
   * The content of the label next to the arrow.
   */
  children?: React.ReactNode;
  /**
   * Whether to horizontally flip the arrow.
   */
  horizontalFlip?: boolean;
  /**
   * Additional class name for the arrow container.
   */
  className?: string;
};

export const Arrow = ({ direction, children, horizontalFlip, className }: ArrowProps) => {
  const lineMaskRef = useRef<SVGPathElement>(null!);
  const arrowMaskRef = useRef<SVGPathElement>(null!);

  useEffect(() => {
    if (lineMaskRef.current) {
      const length = lineMaskRef.current.getTotalLength();
      lineMaskRef.current.style.strokeDasharray = length.toString();
      lineMaskRef.current.style.strokeDashoffset = length.toString();
      lineMaskRef.current.style.stroke = '#ffffff';
      lineMaskRef.current.style.animation = `${styles.drawing} 0.5s linear 1s forwards`;
    }
    if (arrowMaskRef.current) {
      const length = arrowMaskRef.current.getTotalLength();
      arrowMaskRef.current.style.strokeDasharray = length.toString();
      arrowMaskRef.current.style.strokeDashoffset = length.toString();
      arrowMaskRef.current.style.stroke = '#ffffff';
      arrowMaskRef.current.style.animation = `${styles.drawing} 0.2s linear 1.6s forwards`;
    }
  }, [direction]);

  switch (direction) {
    case 'up':
      return (
        <div className={clsx(styles.container, className)}>
          <ArrowUp lineMaskRef={lineMaskRef} arrowMaskRef={arrowMaskRef} horizontalFlip={horizontalFlip} />
          <div
            className={clsx(
              styles['text-container'],
              horizontalFlip ? styles['text-container-up-flipped'] : styles['text-container-up'],
            )}
          >
            {children}
          </div>
        </div>
      );
    case 'down':
      return (
        <div className={clsx(styles.container, className)}>
          <ArrowDown lineMaskRef={lineMaskRef} arrowMaskRef={arrowMaskRef} horizontalFlip={horizontalFlip} />
          <div
            className={clsx(
              styles['text-container'],
              horizontalFlip ? styles['text-container-down-flipped'] : styles['text-container-down'],
            )}
          >
            {children}
          </div>
        </div>
      );
    case 'left':
      return (
        <div className={clsx(styles.container, className)}>
          <ArrowLeft lineMaskRef={lineMaskRef} arrowMaskRef={arrowMaskRef} horizontalFlip={horizontalFlip} />
          <div
            className={clsx(
              styles['text-container'],
              horizontalFlip ? styles['text-container-left-flipped'] : styles['text-container-left'],
            )}
          >
            {children}
          </div>
        </div>
      );
    case 'right':
      return (
        <div className={clsx(styles.container, className)}>
          <ArrowRight lineMaskRef={lineMaskRef} arrowMaskRef={arrowMaskRef} horizontalFlip={horizontalFlip} />
          <div
            className={clsx(
              styles['text-container'],
              horizontalFlip ? styles['text-container-right-flipped'] : styles['text-container-right'],
            )}
          >
            {children}
          </div>
        </div>
      );
  }
};
